import { assertUrlAllowed } from './url-guard.js'

const DEFAULT_HEADERS = {
  Accept: '*/*',
  'Content-type': 'application/json',
}
const DEFAULT_CREDENTIALS = { credentials: 'include' }

/**
 * Validates a request target and performs the fetch through a single, audited
 * choke point. This is the SSRF control for the application: every outbound
 * fetch flows through here so the destination URL is validated (via the shared
 * url-guard policy) before the request is issued.
 */
export async function safeFetch(url, options) {
  // Issue the request against the validated, fully-resolved URL rather than the
  // raw input so the end-result URL is exactly what passed validation.
  const resolvedHref = assertUrlAllowed(url)
  return fetch(resolvedHref, options)
}

export async function fetchJsonData(
  url,
  headers = DEFAULT_HEADERS,
  credentials = DEFAULT_CREDENTIALS
) {
  const options = {
    method: 'get',
    headers,
  }

  if (credentials) {
    Object.assign(options, credentials)
  }

  const response = await safeFetch(url, options)
  dispatchServiceCalled(url)
  if (response.ok) {
    return JSON.parse(await response.text())
  } else {
    return response
  }
}

export async function fetchJsonRawReponse(url, headers = DEFAULT_HEADERS) {
  const response = await safeFetch(url, {
    method: 'get',
    headers,
    credentials: 'include',
  })
  dispatchServiceCalled(url)
  return response
}

export async function postJsonData(url, data, headers = DEFAULT_HEADERS) {
  const response = await safeFetch(url, {
    method: 'post',
    headers,
    body: JSON.stringify(data),
    credentials: 'include',
  })
  dispatchServiceCalled(url)
  const responseText = await response.text()

  if (responseText) {
    return JSON.parse(responseText)
  } else {
    return response
  }
}

export async function postJsonRawResponse(
  url,
  data = {},
  headers = DEFAULT_HEADERS
) {
  const options = {
    method: 'post',
    headers,
    credentials: 'include',
  }

  if (data !== null && (Array.isArray(data) || data instanceof Object)) {
    options.body = JSON.stringify(data)
  }
  const response = await safeFetch(url, options)
  dispatchServiceCalled(url)
  return response
}

export async function putJsonData(url, data, headers = DEFAULT_HEADERS) {
  const response = await safeFetch(url, {
    method: 'put',
    headers,
    body: JSON.stringify(data),
    credentials: 'include',
  })
  dispatchServiceCalled(url)
  const responseText = await response.text()
  if (responseText) {
    return JSON.parse(responseText)
  } else {
    return response
  }
}

export async function postJsonDataBlankResponse(url, data, headers = {}) {
  const response = await safeFetch(url, {
    method: 'post',
    headers: {
      Accept: 'application/json',
      'Content-type': 'application/json',
      ...headers,
    },
    body: JSON.stringify(data),
    credentials: 'include',
  })
  dispatchServiceCalled(url)
  return response
}

function dispatchServiceCalled(url) {
  const event = new CustomEvent('triggerServiceCalled', {
    detail: {
      url: url,
    },
  })
  document.body && document.body.dispatchEvent(event)
}
